import React, { useState, useEffect } from 'react';
import {
  BarChart2,
  TrendingUp,
  Brain,
  ClipboardList,
  Gauge,
  Users,
  MessageSquare,
  BarChart,
  PieChart,
  Timer,
  GitBranch,
  LineChart,
  Star,
  DollarSign,
  Network,
  Clock,
  User,
  Repeat,
  LayoutDashboard,
  Shield,
  Download,
  Tag,
  Loader2,
  X,
  AlertCircle,
  ChevronDown,
  Phone,
  Headphones,
} from 'lucide-react';

import { OverviewDashboard } from '../components/Reports/OverviewDashboard';
import { ConversationAnalytics } from '../components/Reports/ConversationAnalytics';
import { SentimentAnalysis } from '../components/Reports/SentimentAnalysis';
import { AgentPerformance } from '../components/Reports/AgentPerformance';
import { ConversationAudits } from '../components/Reports/ConversationAudits';
import { CSATAnalytics } from '../components/Reports/CSATAnalytics';
import { ChannelAnalytics } from '../components/Reports/ChannelAnalytics';
import { AggregatedReports } from '../components/Reports/AggregatedReports';
import CustomerJourneyAnalytics from '../components/Reports/CustomerJourneyAnalytics';
import SLAComplianceTracking from '../components/Reports/SLAComplianceTracking';
import TeamPerformanceAnalytics from '../components/Reports/TeamPerformanceAnalytics';
import PredictiveAnalytics from '../components/Reports/PredictiveAnalytics';
import ConversationQualityScoring from '../components/Reports/ConversationQualityScoring';
import RevenueImpactAnalysis from '../components/Reports/RevenueImpactAnalysis';
import MultiChannelAttribution from '../components/Reports/MultiChannelAttribution';
import PeakLoadCapacityPlanning from '../components/Reports/PeakLoadCapacityPlanning';
import AgentProductivityWorkload from '../components/Reports/AgentProductivityWorkload';
import CustomerRetentionChurn from '../components/Reports/CustomerRetentionChurn';
import { LabelAnalytics } from '../components/Reports/LabelAnalytics';
// 2026-10-04 (Docs/AI_CALL_CENTER_PRODUCTION_READINESS_PLAN.md 3.2/3.3)
import CallCenterDashboard from '../components/Reports/CallCenterDashboard';
import CallReview from '../components/Reports/CallReview';
import { agentsAPI, inboxesAPI } from '../services/api';
import { PermissionGuard } from '../components/Permissions/PermissionGuard';
import {
  exportToCSV,
  exportToExcel,
  exportToPDFWithScreenshot,
  extractTableDataFromDOM,
  ExportData,
} from '../utils/exportUtils';
import { cn } from '../lib/utils';

import { Button } from '../components/ui/button';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Input } from '../components/ui/input';
import { Separator } from '../components/ui/separator';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

// ─── Types ────────────────────────────────────────────────────────────────────

interface ReportTab {
  label: string;
  icon: React.ReactElement;
  component: React.ReactElement;
  description?: string;
}

interface ReportCategory {
  label: string;
  icon: React.ReactElement;
  description: string;
  tabs: ReportTab[];
}

type DatePreset = '7d' | '30d' | '90d' | 'Custom';

const DATE_PRESETS: { label: DatePreset; days: number | null }[] = [
  { label: '7d',     days: 7  },
  { label: '30d',    days: 30 },
  { label: '90d',    days: 90 },
  { label: 'Custom', days: null },
];

const toDateString = (d: Date) => d.toISOString().split('T')[0];

// ─── ReportsPage ──────────────────────────────────────────────────────────────

const ReportsPage: React.FC = () => {
  // Active report: { cat = category index, tab = sub-report index }
  const [activeReport, setActiveReport] = useState<{ cat: number; tab: number }>({ cat: 0, tab: 0 });
  // Which category groups are expanded in the left nav
  const [openCats, setOpenCats] = useState<Set<number>>(new Set([0, 1, 2, 3, 4, 5, 6]));

  const [loading] = useState(false);
  const [error, setError]             = useState<string | null>(null);
  const [activePreset, setActivePreset] = useState<DatePreset>('30d');
  const [dateRange, setDateRange]     = useState({
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
    endDate:   new Date(),
  });
  const [selectedAgent,   setSelectedAgent]   = useState<number | 'all'>('all');
  const [selectedChannel, setSelectedChannel] = useState<number | 'all'>('all');
  const [agents,   setAgents]   = useState<any[]>([]);
  const [channels, setChannels] = useState<any[]>([]);
  const [filtersLoading, setFiltersLoading] = useState(true);

  useEffect(() => {
    const fetchFilterOptions = async () => {
      setFiltersLoading(true);
      try {
        const [agentsData, channelsData] = await Promise.all([
          agentsAPI.getAgents(),
          inboxesAPI.getInboxes(),
        ]);
        setAgents(agentsData.agents || agentsData || []);
        setChannels(channelsData.inboxes || channelsData || []);
      } catch (err: any) {
        console.error('Failed to load filter options:', err);
        setError('Failed to load filter options. Using defaults.');
      } finally {
        setFiltersLoading(false);
      }
    };
    fetchFilterOptions();
  }, []);

  const applyPreset = (preset: DatePreset) => {
    setActivePreset(preset);
    if (preset === 'Custom') return;
    const days = preset === '7d' ? 7 : preset === '30d' ? 30 : 90;
    setDateRange({
      startDate: new Date(Date.now() - days * 24 * 60 * 60 * 1000),
      endDate:   new Date(),
    });
  };

  const toggleCat = (catIdx: number) => {
    setOpenCats((prev) => {
      const next = new Set(prev);
      if (next.has(catIdx)) next.delete(catIdx); else next.add(catIdx);
      return next;
    });
  };

  const handleExport = async (format: 'csv' | 'pdf' | 'excel') => {
    try {
      const currentCategory = reportCategories[activeReport.cat];
      const currentReport   = currentCategory.tabs[activeReport.tab];

      console.log(`Export: Category ${activeReport.cat}, Tab ${activeReport.tab}, Report: ${currentReport.label}`);

      const panel = document.getElementById(`reports-tabpanel-${activeReport.tab}`);

      if (!panel) {
        const availablePanels = Array.from(document.querySelectorAll('[role="tabpanel"]')).map((p) => p.id);
        console.error('Panel not found. Available panels:', availablePanels);
        throw new Error(`Could not find report panel with index ${activeReport.tab}.`);
      }

      const isHidden = panel.hasAttribute('hidden') && panel.getAttribute('hidden') !== 'false';
      if (isHidden) {
        throw new Error(`Report panel ${activeReport.tab} is not currently visible.`);
      }

      const visiblePanel  = panel as HTMLElement;
      const reportName    = `${currentCategory.label} - ${currentReport.label}`;
      const reportDateRange = {
        startDate: toDateString(dateRange.startDate),
        endDate:   toDateString(dateRange.endDate),
      };

      if (format === 'pdf') {
        await exportToPDFWithScreenshot(reportName, visiblePanel, reportDateRange);
        return;
      }

      const exportData: ExportData = {
        reportName,
        dateRange:      reportDateRange,
        metrics:        [],
        tables:         [],
        charts:         [],
        additionalInfo: currentReport.description,
      };

      const metricCards = visiblePanel.querySelectorAll('.MuiCard-root');
      metricCards.forEach((card) => {
        const label = card.querySelector('.MuiTypography-body2, .MuiTypography-caption')?.textContent?.trim();
        const value = card.querySelector('.MuiTypography-h4, .MuiTypography-h5, .MuiTypography-h6, .MuiTypography-h3')?.textContent?.trim();
        if (label && value) {
          const labelLower = label.toLowerCase();
          if (!labelLower.includes('report') && !labelLower.includes('generated') && !labelLower.includes('copyright') && value !== currentReport.label) {
            exportData.metrics?.push({ label, value });
          }
        }
      });

      const tables = visiblePanel.querySelectorAll('table.MuiTable-root');
      tables.forEach((table, index) => {
        const tableElement = table as HTMLTableElement;
        const tableData    = extractTableDataFromDOM(tableElement);
        if (tableData.headers.length > 0 && tableData.rows.length > 0) {
          let titleElement = table.closest('.MuiPaper-root')?.querySelector('.MuiTypography-h6, .MuiTypography-h5, .MuiTypography-h4');
          if (!titleElement) {
            titleElement = table.parentElement?.querySelector('.MuiTypography-h6, .MuiTypography-h5, .MuiTypography-h4') || null;
          }
          const title = titleElement?.textContent?.trim() || `Data Table ${index + 1}`;
          exportData.tables?.push({ title, headers: tableData.headers, rows: tableData.rows });
        }
      });

      if (exportData.tables?.length === 0 && exportData.metrics?.length === 0) {
        exportData.additionalInfo = `${exportData.additionalInfo}\n\nNote: This report contains primarily visual charts. For complete visual export, please use PDF format.`;
      }

      switch (format) {
        case 'csv':   exportToCSV(exportData);   break;
        case 'excel': exportToExcel(exportData); break;
        default:      throw new Error('Invalid export format');
      }

      console.log(`Successfully exported ${currentReport.label} as ${format.toUpperCase()}`);
    } catch (error) {
      console.error('Export error:', error);
      alert(`Failed to export report.\n\nError: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  // ── Report registry ──────────────────────────────────────────────────────────

  const reportCategories: ReportCategory[] = [
    {
      label: 'Overview & Executive',
      icon: <LayoutDashboard className="h-4 w-4" />,
      description: 'Big-picture summaries for leadership and analytics overviews',
      tabs: [
        { label: 'Overview',             icon: <BarChart2 className="h-4 w-4" />,      component: <OverviewDashboard dateRange={dateRange} />,                                                                                           description: 'All-in-one executive summary of key metrics' },
        { label: 'Aggregated Reports',   icon: <BarChart className="h-4 w-4" />,        component: <AggregatedReports dateRange={dateRange} />,                                                                                          description: 'Consolidated data across teams, channels, or time periods' },
        { label: 'Revenue Impact',       icon: <DollarSign className="h-4 w-4" />,      component: <RevenueImpactAnalysis startDate={toDateString(dateRange.startDate)} endDate={toDateString(dateRange.endDate)} />,                    description: 'How conversations, agents, and channels contribute to revenue' },
        { label: 'Predictive Analytics', icon: <LineChart className="h-4 w-4" />,       component: <PredictiveAnalytics startDate={toDateString(dateRange.startDate)} endDate={toDateString(dateRange.endDate)} forecastDays={30} />,   description: 'AI-driven trends, forecasts, and what-if scenarios' },
      ],
    },
    {
      label: 'Conversation Intelligence',
      icon: <MessageSquare className="h-4 w-4" />,
      description: 'Deep-dive into conversations, tone, and compliance',
      tabs: [
        { label: 'Conversation Analytics', icon: <BarChart2 className="h-4 w-4" />,    component: <ConversationAnalytics dateRange={dateRange} selectedAgent={selectedAgent} selectedChannel={selectedChannel} />, description: 'Volume, topics, durations, and engagement trends' },
        { label: 'Sentiment Analysis',     icon: <Brain className="h-4 w-4" />,         component: <SentimentAnalysis dateRange={dateRange} selectedAgent={selectedAgent} selectedChannel={selectedChannel} />,    description: 'Sentiment breakdown by customer tone and context' },
        { label: 'Conversation Quality',   icon: <Star className="h-4 w-4" />,          component: <ConversationQualityScoring startDate={toDateString(dateRange.startDate)} endDate={toDateString(dateRange.endDate)} />,              description: 'Quality and adherence metrics' },
        { label: 'Conversation Audits',    icon: <ClipboardList className="h-4 w-4" />, component: <ConversationAudits dateRange={dateRange} selectedAgent={selectedAgent} />,                                                          description: 'Manual/AI audits for compliance and QA scoring' },
        { label: 'Label Analytics',        icon: <Tag className="h-4 w-4" />,           component: <LabelAnalytics dateRange={dateRange} selectedAgent={selectedAgent} selectedChannel={selectedChannel} />,       description: 'Label usage, distribution, and performance analytics' },
      ],
    },
    {
      label: 'Agent & Team Performance',
      icon: <Users className="h-4 w-4" />,
      description: 'Track how individuals and teams perform operationally',
      tabs: [
        { label: 'Agent Performance',  icon: <User className="h-4 w-4" />,  component: <AgentPerformance dateRange={dateRange} />,                                                                                                      description: 'Agent-specific KPIs' },
        { label: 'Team Performance',   icon: <Users className="h-4 w-4" />, component: <TeamPerformanceAnalytics startDate={toDateString(dateRange.startDate)} endDate={toDateString(dateRange.endDate)} />,                            description: 'Aggregated metrics per team or department' },
        { label: 'Agent Productivity', icon: <Gauge className="h-4 w-4" />, component: <AgentProductivityWorkload startDate={toDateString(dateRange.startDate)} endDate={toDateString(dateRange.endDate)} />,                           description: 'Workload balance, idle time, occupancy' },
        { label: 'Capacity Planning',  icon: <Clock className="h-4 w-4" />, component: <PeakLoadCapacityPlanning startDate={toDateString(dateRange.startDate)} endDate={toDateString(dateRange.endDate)} />,                            description: 'Forecast staffing needs vs conversation volume' },
      ],
    },
    {
      label: 'Customer Experience',
      icon: <Star className="h-4 w-4" />,
      description: 'Gauge happiness, loyalty, and end-to-end journey',
      tabs: [
        { label: 'CSAT Analytics',      icon: <TrendingUp className="h-4 w-4" />, component: <CSATAnalytics dateRange={dateRange} />,                                                                                                   description: 'Satisfaction scores and breakdown' },
        { label: 'Customer Journey',    icon: <GitBranch className="h-4 w-4" />,  component: <CustomerJourneyAnalytics startDate={toDateString(dateRange.startDate)} endDate={toDateString(dateRange.endDate)} channelId={selectedChannel !== 'all' ? String(selectedChannel) : undefined} />, description: 'Touchpoint mapping from first contact to resolution' },
        { label: 'Customer Retention',  icon: <Repeat className="h-4 w-4" />,     component: <CustomerRetentionChurn startDate={toDateString(dateRange.startDate)} endDate={toDateString(dateRange.endDate)} />,                        description: 'Churn rates, returning customers, and loyalty metrics' },
      ],
    },
    {
      label: 'Channel & Attribution',
      icon: <Network className="h-4 w-4" />,
      description: 'Measure which channels perform best and why',
      tabs: [
        { label: 'Channel Analytics',         icon: <PieChart className="h-4 w-4" />, component: <ChannelAnalytics dateRange={dateRange} />,                                                                                            description: 'Channel-wise breakdown' },
        { label: 'Multi-Channel Attribution', icon: <Network className="h-4 w-4" />,  component: <MultiChannelAttribution startDate={toDateString(dateRange.startDate)} endDate={toDateString(dateRange.endDate)} />,                  description: 'Conversion and engagement attribution across touchpoints' },
      ],
    },
    {
      label: 'Compliance & Operations',
      icon: <Shield className="h-4 w-4" />,
      description: 'Monitor SLA adherence, standards, and operational health',
      tabs: [
        { label: 'SLA Compliance', icon: <Timer className="h-4 w-4" />, component: <SLAComplianceTracking startDate={toDateString(dateRange.startDate)} endDate={toDateString(dateRange.endDate)} agentId={selectedAgent !== 'all' ? String(selectedAgent) : undefined} />, description: 'SLA metrics, breaches, and response/resolution timelines' },
      ],
    },
    // 2026-10-04: last on purpose -- the active report is tracked by index,
    // so existing categories keep their positions.
    {
      label: 'AI Call Center',
      icon: <Phone className="h-4 w-4" />,
      description: 'WhatsApp AI calls: volume, speed, transfers and call review',
      tabs: [
        { label: 'Call Dashboard', icon: <Gauge className="h-4 w-4" />,      component: <CallCenterDashboard dateRange={dateRange} />, description: 'Answer rate, time to first reply, where the time goes, failures' },
        { label: 'Call Review',    icon: <Headphones className="h-4 w-4" />, component: <CallReview dateRange={dateRange} />,          description: 'Every call turn by turn, with transcript and recorded audio' },
      ],
    },
  ];

  const activeCat    = reportCategories[activeReport.cat];
  const activeTab    = activeCat.tabs[activeReport.tab];

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-1 flex-col">

      {/* ── Sticky top bar: title + filters + export ────────────────────────
           One compact header — no card chrome, no tab row.               */}
      <div className="sticky top-0 z-20 bg-background/98 backdrop-blur-sm border-b border-border">

        {/* Row 1: Page title + Export */}
        <div className="flex items-center justify-between gap-4 px-4 lg:px-6 pt-3 pb-1.5">
          <div className="min-w-0">
            <h1 className="text-xl font-semibold tracking-tight">Analytics & Reports</h1>
            <p className="text-xs text-muted-foreground mt-0.5 truncate">
              {activeTab.label} — {activeCat.description}
            </p>
          </div>
          <PermissionGuard permission="reports:export">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="shrink-0 h-8 gap-1.5">
                  <Download className="h-3.5 w-3.5" />
                  Export
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleExport('csv')}>
                  <Download className="h-4 w-4 mr-2" />Export as CSV
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleExport('pdf')}>
                  <Download className="h-4 w-4 mr-2" />Export as PDF
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleExport('excel')}>
                  <Download className="h-4 w-4 mr-2" />Export as Excel
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </PermissionGuard>
        </div>

        {/* Row 2: Filters */}
        <div className="flex flex-wrap items-center gap-2 px-4 lg:px-6 pb-2.5">

          {/* Date preset segment */}
          <div className="flex items-center gap-0.5 rounded-md border border-border bg-muted/50 p-0.5">
            {DATE_PRESETS.map((p) => (
              <button
                key={p.label}
                type="button"
                onClick={() => applyPreset(p.label)}
                className={cn(
                  'px-2.5 py-1 text-xs font-medium rounded transition-colors',
                  activePreset === p.label
                    ? 'bg-background shadow-sm text-foreground'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                {p.label}
              </button>
            ))}
          </div>

          {/* Custom date inputs */}
          {activePreset === 'Custom' && (
            <div className="flex items-center gap-1.5 animate-in slide-in-from-left-2 duration-150">
              <Input
                type="date"
                value={toDateString(dateRange.startDate)}
                onChange={(e) =>
                  setDateRange((prev) => ({ ...prev, startDate: new Date(e.target.value) }))
                }
                className="h-7 w-[136px] text-xs"
              />
              <span className="text-muted-foreground text-xs">–</span>
              <Input
                type="date"
                value={toDateString(dateRange.endDate)}
                onChange={(e) =>
                  setDateRange((prev) => ({ ...prev, endDate: new Date(e.target.value) }))
                }
                className="h-7 w-[136px] text-xs"
              />
            </div>
          )}

          <Separator orientation="vertical" className="h-4" />

          {/* Agent filter */}
          <Select
            value={String(selectedAgent)}
            onValueChange={(v) => setSelectedAgent(v === 'all' ? 'all' : Number(v))}
            disabled={filtersLoading}
          >
            <SelectTrigger className="h-7 w-[130px] text-xs">
              <SelectValue placeholder="All Agents" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Agents</SelectItem>
              {agents.map((agent) => (
                <SelectItem key={agent.id} value={String(agent.id)}>
                  {agent.name || agent.email}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Channel filter */}
          <Select
            value={String(selectedChannel)}
            onValueChange={(v) => setSelectedChannel(v === 'all' ? 'all' : Number(v))}
            disabled={filtersLoading}
          >
            <SelectTrigger className="h-7 w-[130px] text-xs">
              <SelectValue placeholder="All Channels" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Channels</SelectItem>
              {channels.map((channel) => (
                <SelectItem key={channel.id} value={String(channel.id)}>
                  {channel.name || channel.channel_type}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

        </div>
      </div>

      {/* Error alert */}
      {error && (
        <div className="px-4 lg:px-6 pt-3">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription className="flex items-center justify-between">
              {error}
              <Button
                variant="ghost"
                size="icon"
                className="h-5 w-5 shrink-0 ml-2"
                onClick={() => setError(null)}
              >
                <X className="h-3 w-3" />
              </Button>
            </AlertDescription>
          </Alert>
        </div>
      )}

      {/* ── Body: left nav + content ─────────────────────────────────────── */}
      <div className="flex flex-1">

        {/* ── Left nav ──────────────────────────────────────────────────────
             Pattern: Vercel Analytics / Linear — icon container header,
             tree-connector guide line, fill-style active state.          */}
        <nav className="w-[260px] shrink-0 border-r border-border bg-muted/20 overflow-y-auto px-2 py-3 space-y-0.5">
          {reportCategories.map((cat, catIdx) => {
            const isOpen      = openCats.has(catIdx);
            const hasCatActive = activeReport.cat === catIdx;
            return (
              <div key={catIdx}>

                {/* ── Category header ────────────────────────────────────────
                     Icon container (colored when category is active) +
                     label + sub-report count + collapse chevron           */}
                <button
                  type="button"
                  onClick={() => toggleCat(catIdx)}
                  className="group flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left transition-colors hover:bg-accent/60"
                >
                  {/* Icon container */}
                  <span className={cn(
                    'flex h-6 w-6 shrink-0 items-center justify-center rounded-md transition-colors',
                    hasCatActive
                      ? 'bg-primary/15 text-primary'
                      : 'bg-background text-muted-foreground group-hover:bg-accent group-hover:text-foreground border border-border/60',
                  )}>
                    {React.cloneElement(cat.icon as React.ReactElement<any>, { className: 'h-3.5 w-3.5' })}
                  </span>

                  {/* Label */}
                  <span className={cn(
                    'flex-1 truncate text-sm font-medium leading-none',
                    hasCatActive ? 'text-foreground' : 'text-muted-foreground',
                  )}>
                    {cat.label}
                  </span>

                  {/* Sub-report count */}
                  <span className="text-[11px] tabular-nums text-muted-foreground/40 font-medium mr-0.5">
                    {cat.tabs.length}
                  </span>

                  {/* Chevron */}
                  <ChevronDown className={cn(
                    'h-3.5 w-3.5 shrink-0 text-muted-foreground/40 transition-transform duration-200',
                    isOpen && 'rotate-180',
                  )} />
                </button>

                {/* ── Sub-report items — animated collapse ─────────────────
                     Flat indent — no tree line, maximum text width.      */}
                <div className={cn(
                  'grid transition-[grid-template-rows] duration-200 ease-in-out',
                  isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
                )}>
                  <div className="overflow-hidden">
                    <div className="pl-8 pr-1 mt-0.5 mb-2 space-y-px">
                      {cat.tabs.map((tab, tabIdx) => {
                        const isActive = hasCatActive && activeReport.tab === tabIdx;
                        return (
                          <button
                            key={tabIdx}
                            type="button"
                            title={tab.label}
                            onClick={() => setActiveReport({ cat: catIdx, tab: tabIdx })}
                            className={cn(
                              'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors',
                              isActive
                                ? 'bg-primary/10 text-primary font-medium'
                                : 'text-muted-foreground hover:text-foreground hover:bg-accent/60',
                            )}
                          >
                            <span className={cn(
                              'shrink-0 transition-colors',
                              isActive ? 'text-primary' : 'text-muted-foreground/40',
                            )}>
                              {React.cloneElement(tab.icon as React.ReactElement<any>, { className: 'h-3.5 w-3.5' })}
                            </span>
                            <span className="truncate">{tab.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>

              </div>
            );
          })}
        </nav>

        {/* ── Content area ──────────────────────────────────────────────── */}
        <div className="flex-1 min-w-0">
          {loading ? (
            <div className="flex items-center justify-center p-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div
              role="tabpanel"
              id={`reports-tabpanel-${activeReport.tab}`}
              aria-label={activeTab.label}
              className="px-5 lg:px-7 py-5"
            >
              {activeTab.component}
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default ReportsPage;
